import React, { useRef, useState, useEffect, useLayoutEffect } from "react";

const hexToRgba = (hex, alpha = 1) => {
  if (!hex) return `rgba(0,0,0,${alpha})`;
  const parsed = hex.replace("#", "");
  const bigint = parseInt(parsed, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r},${g},${b},${alpha})`;
};

// Helper to determine how many tag elements fit in the container
function getVisibleChildren(container, refWidth) {
  let totalWidth = 0;
  let count = 0;
  const spacingPerChild = 4; // matches gap-1 in Tailwind (0.25rem)
  const reservedWidthForBox = 34; // approximate width for the "+N" span (px)

  // only measure actual tag spans (exclude the "+N" counter)
  const tagElements = Array.from(container.querySelectorAll('[data-role="tag"]'));

  for (let i = 0; i < tagElements.length; i++) {
    const childWidth = tagElements[i].getBoundingClientRect().width;
    totalWidth += childWidth;
    count++;

    // include spacing for each rendered child after the first
    const totalWithSpacing = totalWidth + spacingPerChild * (count - 1);

    // if adding reserved box overflows, exclude current and stop
    if (totalWithSpacing + reservedWidthForBox > refWidth) {
      return Math.max(0, count - 1);
    }
  }

  // all tag items fit without needing box
  return count;
}

const ResponsiveTags = ({ parsedValue = [] }) => {
  const containerRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(parsedValue.length);

  useLayoutEffect(() => {
    const calculateVisibleTags = () => {
      const container = containerRef.current;
      if (!container) return;

      const containerWidth = container.clientWidth;
      const fitCount = getVisibleChildren(container, containerWidth);
      setVisibleCount(fitCount);
    };

    // initial calculation
    calculateVisibleTags();

    const observer = new ResizeObserver(calculateVisibleTags);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [parsedValue]);

  const remaining = parsedValue.length - visibleCount;

  return (
    <p ref={containerRef} className="w-full flex gap-1 overflow-hidden truncate">
      {parsedValue.slice(0, visibleCount).map((item, index) => {
        const rgbaColor = item?.color_code || item?.code || item?.color;
        return (
          <span
            data-role="tag"
            key={index}
            style={{
              backgroundColor: hexToRgba(rgbaColor, 0.1),
              color: rgbaColor ? hexToRgba(rgbaColor) : "black",
              border: rgbaColor ? `0.8px solid ${rgbaColor}` : "none",
              borderRadius: "5px",
              whiteSpace: "nowrap",
            }}
            className="px-2 py-1 rounded"
          >
            {item?.value || item?.label}
          </span>
        );
      })}
      {remaining > 0 && <span className="px-2 py-1 rounded text-gray-800 whitespace-nowrap">+{remaining}</span>}
    </p>
  );
};

export default ResponsiveTags;
